import { useEffect, useMemo, useState } from "react"
import { createFileRoute } from "@tanstack/react-router"
import { useQuery } from "@tanstack/react-query"
import { AtSign, Loader2, SearchX } from "lucide-react"
import { resolveEventId, useActiveEvent } from "@/core/active-event"
import {
  MY_ACTIVITY_FILTERS,
  MY_ACTIVITY_FILTER_LABEL,
  myActivityFeedQueryOptions,
  type MyActivityFilter,
  type MyActivityItem,
} from "@/core/my-activity"
import {
  conversationsQueryOptions,
  dmTitle,
  personInitials,
  previewText,
} from "@/core/conversations"
import { isTicketOpen, ticketsQueryOptions } from "@/core/tickets"
import { relTime } from "@/core/channels"
import { getSessionUser } from "@/core/auth"
import { useMyActivity } from "@/lib/use-my-activity"
import { useRightRail } from "@/lib/right-rail"
import { YouRow } from "@/components/you/YouRow"
import { UnreadList, useUnreadBuckets } from "@/components/you/UnreadList"
import { UnreadCount, UnreadDot } from "@/components/ui/UnreadMark"
import { ActionBar } from "@/components/ui/ActionBar"
import { cn } from "@/core/utils"

// Tab set = the server feed filters + the client-composed "Unread" view (a
// Slack-style Unreads list of surfaces, built from the shell's live queries).
type YouTab = MyActivityFilter | "unread"
const YOU_TABS: readonly YouTab[] = [
  "all",
  "unread",
  ...MY_ACTIVITY_FILTERS.filter((f) => f !== "all"),
]
const YOU_TAB_LABEL: Record<YouTab, string> = {
  ...MY_ACTIVITY_FILTER_LABEL,
  unread: "Unread",
}

// Warm the default ("all") tab on the server so the first paint has data.
export const Route = createFileRoute("/$orgSlug/$eventSlug/you")({
  // The tab is URL-driven (`?tab=unread`) so the menubar / G-U chord can deep
  // link straight to a view; a bad value falls back to "all".
  validateSearch: (search: Record<string, unknown>): { tab?: YouTab } =>
    YOU_TABS.includes(search.tab as YouTab) && search.tab !== "all"
      ? { tab: search.tab as YouTab }
      : {},
  loader: ({ context, params }) => {
    void resolveEventId(params.orgSlug, params.eventSlug).then((eventId) => {
      if (!eventId) return
      void context.queryClient
        .ensureInfiniteQueryData(myActivityFeedQueryOptions(eventId, "all"))
        .catch(() => undefined)
    })
  },
  component: YouView,
})

function YouView() {
  const { eventId } = useActiveEvent()
  const params = Route.useParams()
  const navigate = Route.useNavigate()
  const filter: YouTab = Route.useSearch().tab ?? "all"
  const setFilter = (tab: YouTab) =>
    void navigate({ search: tab === "all" ? {} : { tab }, replace: true })

  // Search: the input updates immediately (controlled field) while the value
  // that actually drives the query is debounced, so typing doesn't fire a fetch
  // per keystroke.
  const [searchInput, setSearchInput] = useState("")
  const [debouncedQuery, setDebouncedQuery] = useState("")
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(searchInput.trim()), 280)
    return () => clearTimeout(t)
  }, [searchInput])

  // "unread" is client-composed (no server source) — keep the feed hook on the
  // already-warmed "all" filter so hook order is stable and nothing refetches.
  const feed = useMyActivity(eventId, filter === "unread" ? "all" : filter, debouncedQuery)
  const unread = useUnreadBuckets(eventId, debouncedQuery)
  const searching = debouncedQuery.length > 0

  // Tab counts come from the STABLE shared caches (tickets list / unread
  // buckets), not the per-tab feed query — switching tabs must never blank or
  // refetch the numbers. Same rule as the server badge: assigned + not closed.
  const meId = getSessionUser()?.id ?? null
  const ticketsQ = useQuery({ ...ticketsQueryOptions(eventId ?? ""), enabled: !!eventId })
  const assignedCount = useMemo(
    () =>
      meId
        ? (ticketsQ.data ?? []).filter((t) => t.assigneeUserId === meId && isTicketOpen(t)).length
        : 0,
    [ticketsQ.data, meId],
  )

  // Threads tab: collapse the per-reply activity into one row PER THREAD (its
  // latest activity), unread-first then most-recent — so it reads as a list of
  // threads, not individual reply messages.
  const items = useMemo(() => {
    if (filter === "threads") {
      const byRoot = new Map<string, MyActivityItem>()
      for (const it of feed.items) {
        const root = it.parentMessageId ?? it.id
        const prev = byRoot.get(root)
        if (!prev) {
          byRoot.set(root, it)
        } else {
          const newer = it.occurredAt > prev.occurredAt ? it : prev
          byRoot.set(root, { ...newer, unread: it.unread || prev.unread })
        }
      }
      return [...byRoot.values()].sort(
        (a, b) => (b.unread ? 1 : 0) - (a.unread ? 1 : 0) || b.occurredAt.localeCompare(a.occurredAt),
      )
    }
    // All tab: collapse DM messages to ONE row per conversation (its latest —
    // the feed is newest-first — OR-ing unread across the thread) so the list
    // reads as conversations, not individual DM messages. The DMs tab still
    // shows the full per-conversation inbox. Non-DM items pass through as-is.
    if (filter === "all") {
      const dmUnread = new Map<string, boolean>()
      for (const it of feed.items) {
        if (it.kind === "dm" && it.conversationId)
          dmUnread.set(it.conversationId, (dmUnread.get(it.conversationId) ?? false) || it.unread)
      }
      const seen = new Set<string>()
      const out: MyActivityItem[] = []
      for (const it of feed.items) {
        if (it.kind === "dm" && it.conversationId) {
          if (seen.has(it.conversationId)) continue
          seen.add(it.conversationId)
          out.push({ ...it, unread: dmUnread.get(it.conversationId) ?? it.unread })
        } else {
          out.push(it)
        }
      }
      return out
    }
    return feed.items
  }, [filter, feed.items])

  return (
    <div className="flex h-full min-h-0 flex-col @container">
      {/* Canvas header (h-[52px], hairline border). Realtime status shows only
          on the menubar avatar dot, so no per-view "Live" badge here. */}
      <header className="flex h-[52px] shrink-0 items-center gap-2.5 border-b border-plum-200/50 px-4 dark:border-white/10">
        <AtSign className="h-[18px] w-[18px] text-[var(--accent)]" />
        <h1 className="truncate text-[15px] font-semibold text-plum-900 dark:text-plum-50">
          {getSessionUser()?.name?.trim() || "Me"}
        </h1>
      </header>

      {/* Toolbar: tabs as Views (All / Mentions / DMs / Threads / Assigned) +
          a Search segment. The "Assigned" view carries its open-ticket count. */}
      <ActionBar
        title="your activity"
        views={YOU_TABS.map((f) => ({
          id: f,
          label: YOU_TAB_LABEL[f],
          count:
            f === "assigned"
              ? assignedCount
              : f === "unread" && unread.total > 0
                ? unread.total
                : undefined,
        }))}
        activeView={filter}
        onView={(id) => setFilter(id as YouTab)}
        search={searchInput}
        onSearch={setSearchInput}
      />

      {/* Feed body */}
      <div className="scroll-thin min-h-0 flex-1 overflow-y-auto">
        {filter === "unread" ? (
          unread.isLoading ? (
            <FeedSkeleton />
          ) : unread.tickets.length + unread.channels.length + unread.dms.length === 0 ? (
            searching ? (
              <NoSearchResults query={debouncedQuery} />
            ) : (
              <EmptyFeed filter="unread" />
            )
          ) : (
            <UnreadList buckets={unread} />
          )
        ) : filter === "dms" ? (
          <DmConversationsList eventId={eventId} query={debouncedQuery} params={params} />
        ) : feed.isLoading ? (
          <FeedSkeleton />
        ) : feed.isError ? (
          <div className="px-4 py-12 text-center text-[12.5px] text-plum-700/60 dark:text-plum-200/45">
            Couldn&apos;t load your activity.{" "}
            <button
              onClick={() => void feed.refetch()}
              className="font-semibold text-plum-700 underline dark:text-plum-200"
            >
              Retry
            </button>
          </div>
        ) : feed.items.length === 0 ? (
          searching ? (
            <NoSearchResults query={debouncedQuery} />
          ) : (
            <EmptyFeed filter={filter} />
          )
        ) : (
          <>
            <ol className="mx-auto w-full max-w-3xl px-2 py-2 @[700px]:px-4">
              {items.map((item) => (
                <YouRow key={`${item.kind}:${item.id}`} item={item} params={params} />
              ))}
            </ol>
            {feed.hasMore && (
              <div className="flex justify-center pb-5">
                <button
                  type="button"
                  disabled={feed.isFetchingNextPage}
                  onClick={() => void feed.fetchNextPage()}
                  className="flex items-center gap-1.5 rounded-lg border border-plum-200/70 px-3 py-1.5 text-[12px] font-semibold text-plum-700/75 transition hover:bg-plum-100/60 disabled:opacity-50 dark:border-white/10 dark:text-plum-200/55 dark:hover:bg-white/5"
                >
                  {feed.isFetchingNextPage && (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  )}
                  Load more
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

// The DMs tab shows your DM CONVERSATIONS (an inbox), not individual message
// activity — open one to read/reply. Mirrors the conversations route's list.
function DmConversationsList({
  eventId,
  query,
}: {
  eventId: string | undefined
  query: string
  params: { orgSlug: string; eventSlug: string }
}) {
  const showInRail = useRightRail((s) => s.show)
  const me = getSessionUser()?.id ?? null
  const q = useQuery({ ...conversationsQueryOptions(eventId ?? ""), enabled: !!eventId })
  // Personal list — only the DMs I'm actually a participant in. Defense-in-depth:
  // the backend list-my-conversations scopes DMs to my membership too, but this
  // keeps the "Me" view correct even against a stale/over-broad cache. (`!me` →
  // don't filter, rather than render an empty list, if the session isn't loaded.)
  const all = (q.data ?? []).filter((c) => !me || c.participants.some((p) => p.userId === me))
  const ql = query.toLowerCase()
  const rows = ql
    ? all.filter((c) => `${c.participants.map((p) => p.displayName).join(" ")} ${c.lastMessage?.body ?? ""}`.toLowerCase().includes(ql))
    : all

  if (q.isLoading) return <FeedSkeleton />
  if (rows.length === 0) return query ? <NoSearchResults query={query} /> : <EmptyFeed filter="dms" />

  return (
    <ol className="mx-auto w-full max-w-3xl px-2 py-2 @[700px]:px-4">
      {rows.map((c) => {
        const title = dmTitle(c, me)
        const unread = c.unread > 0
        return (
          <li key={c.id}>
            <button
              onClick={() => showInRail({ kind: "dm-thread", conversationId: c.id })}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition hover:bg-plum-100/50 dark:hover:bg-white/5"
            >
              <UnreadDot show={unread} reserve />
              <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-plum-100 text-[12px] font-semibold text-plum-700 dark:bg-white/8 dark:text-plum-200">
                {personInitials(title)}
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2">
                  <span
                    className={cn(
                      "min-w-0 flex-1 truncate text-[13.5px] text-plum-900 dark:text-plum-50",
                      unread ? "font-bold" : "font-semibold",
                    )}
                  >
                    {title}
                  </span>
                  {c.lastMessage?.createdAt && (
                    <span className="shrink-0 text-[10.5px] text-plum-700/45 dark:text-plum-200/35">
                      {relTime(c.lastMessage.createdAt)}
                    </span>
                  )}
                </span>
                <span className="mt-0.5 flex items-center gap-2">
                  <span
                    className={cn(
                      "min-w-0 flex-1 truncate text-[12px]",
                      unread
                        ? "font-medium text-plum-800/80 dark:text-plum-100/70"
                        : "text-plum-700/55 dark:text-plum-200/40",
                    )}
                  >
                    {previewText(c.lastMessage)}
                  </span>
                  <UnreadCount n={c.unread} />
                </span>
              </span>
            </button>
          </li>
        )
      })}
    </ol>
  )
}

const EMPTY_COPY: Record<YouTab, { title: string; sub: string }> = {
  all: {
    title: "You're all caught up",
    sub: "Mentions, DMs, thread replies, and tickets assigned to you will show up here.",
  },
  unread: {
    title: "No unreads",
    sub: "Tickets with new activity, unread channels, and unread DMs will collect here.",
  },
  mentions: {
    title: "No mentions",
    sub: "When a teammate @-mentions you in a channel, DM, or ticket, it lands here.",
  },
  dms: {
    title: "No direct messages",
    sub: "New messages in your DMs and group chats will appear here.",
  },
  threads: {
    title: "No thread replies",
    sub: "Replies on threads you started or joined will show up here.",
  },
  assigned: {
    title: "Nothing assigned to you",
    sub: "Support tickets assigned to you will collect here so nothing slips.",
  },
}

function EmptyFeed({ filter }: { filter: YouTab }) {
  const copy = EMPTY_COPY[filter]
  return (
    <div className="flex h-full items-center justify-center p-8">
      <div className="max-w-sm text-center">
        <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-2xl bg-plum-100/70 text-[var(--accent)] dark:bg-white/5">
          <AtSign className="h-5 w-5" />
        </div>
        <h2 className="text-[15px] font-semibold text-plum-900 dark:text-plum-50">
          {copy.title}
        </h2>
        <p className="mt-1.5 text-[13px] leading-relaxed text-plum-700/55 dark:text-plum-200/45">
          {copy.sub}
        </p>
      </div>
    </div>
  )
}

function NoSearchResults({ query }: { query: string }) {
  return (
    <div className="flex h-full items-center justify-center p-8">
      <div className="max-w-sm text-center">
        <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-2xl bg-plum-100/70 text-plum-700/70 dark:bg-white/5 dark:text-plum-200/55">
          <SearchX className="h-5 w-5" />
        </div>
        <h2 className="text-[15px] font-semibold text-plum-900 dark:text-plum-50">
          No matches for “{query}”
        </h2>
        <p className="mt-1.5 text-[13px] leading-relaxed text-plum-700/55 dark:text-plum-200/45">
          Try a different word, or switch tabs to widen the search.
        </p>
      </div>
    </div>
  )
}

function FeedSkeleton() {
  return (
    <div className="mx-auto w-full max-w-3xl space-y-2 px-2 py-3 @[700px]:px-4">
      {Array.from({ length: 7 }).map((_, i) => (
        <div key={i} className="flex gap-3 px-3 py-3">
          <span className="size-9 shrink-0 animate-pulse rounded-xl bg-plum-100/70 dark:bg-white/5" />
          <div className="flex-1 space-y-1.5 pt-1">
            <span className="block h-3 w-2/5 animate-pulse rounded bg-plum-100/70 dark:bg-white/5" />
            <span className="block h-2.5 w-1/4 animate-pulse rounded bg-plum-100/70 dark:bg-white/5" />
            <span className="block h-3 w-3/4 animate-pulse rounded bg-plum-100/70 dark:bg-white/5" />
          </div>
        </div>
      ))}
    </div>
  )
}
